// Type definition for US states and territories
export type USStateOrTerritory = 
  | 'Alabama' | 'Alaska' | 'Arizona' | 'Arkansas' | 'California' | 'Colorado' | 'Connecticut' 
  | 'Delaware' | 'Florida' | 'Georgia' | 'Hawaii' | 'Idaho' | 'Illinois' | 'Indiana' | 'Iowa' 
  | 'Kansas' | 'Kentucky' | 'Louisiana' | 'Maine' | 'Maryland' | 'Massachusetts' | 'Michigan' 
  | 'Minnesota' | 'Mississippi' | 'Missouri' | 'Montana' | 'Nebraska' | 'Nevada' | 'New Hampshire' 
  | 'New Jersey' | 'New Mexico' | 'New York' | 'North Carolina' | 'North Dakota' | 'Ohio' | 'Oklahoma' 
  | 'Oregon' | 'Pennsylvania' | 'Rhode Island' | 'South Carolina' | 'South Dakota' | 'Tennessee' 
  | 'Texas' | 'Utah' | 'Vermont' | 'Virginia' | 'Washington' | 'West Virginia' | 'Wisconsin' | 'Wyoming'
  | 'District of Columbia' | 'American Samoa' | 'Guam' | 'Northern Mariana Islands' | 'Puerto Rico' 
  | 'U.S. Virgin Islands';

// Type definition for US state and territory abbreviations
export type USStateOrTerritoryAbbreviation = 
  | 'AK' | 'AL' | 'AR' | 'AS' | 'AZ' | 'CA' | 'CO' | 'CT' | 'DC' | 'DE' | 'FL' | 'GA' | 'GU' | 'HI' 
  | 'IA' | 'ID' | 'IL' | 'IN' | 'KS' | 'KY' | 'LA' | 'MA' | 'MD' | 'ME' | 'MI' | 'MN' | 'MO' | 'MP' 
  | 'MS' | 'MT' | 'NC' | 'ND' | 'NE' | 'NH' | 'NJ' | 'NM' | 'NV' | 'NY' | 'OH' | 'OK' | 'OR' | 'PA' 
  | 'PR' | 'RI' | 'SC' | 'SD' | 'TN' | 'TX' | 'UT' | 'VA' | 'VI' | 'VT' | 'WA' | 'WI' | 'WV' | 'WY';

 // Mapping of US states and territories to their abbreviations
const STATE_ABBREVIATIONS: Record<USStateOrTerritory, USStateOrTerritoryAbbreviation> = {
  'Alabama': 'AL',
  'Alaska': 'AK',
  'American Samoa': 'AS',
  'Arizona': 'AZ',
  'Arkansas': 'AR',
  'California': 'CA',
  'Colorado': 'CO',
  'Connecticut': 'CT',
  'Delaware': 'DE',
  'District of Columbia': 'DC',
  'Florida': 'FL',
  'Georgia': 'GA',
  'Guam': 'GU',
  'Hawaii': 'HI',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Indiana': 'IN',
  'Iowa': 'IA',
  'Kansas': 'KS',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Maine': 'ME',
  'Maryland': 'MD',
  'Massachusetts': 'MA',
  'Michigan': 'MI',
  'Minnesota': 'MN',
  'Mississippi': 'MS',
  'Missouri': 'MO',
  'Montana': 'MT',
  'Northern Mariana Islands': 'MP',
  'Nebraska': 'NE',
  'Nevada': 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Ohio': 'OH',
  'Oklahoma': 'OK',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Puerto Rico': 'PR',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Vermont': 'VT',
  'Virginia': 'VA',
  'U.S. Virgin Islands': 'VI',
  'Washington': 'WA',
  'West Virginia': 'WV',
  'Wisconsin': 'WI',
  'Wyoming': 'WY'
};

// function to return array of state abbreviations
export function getStateAbbreviations(): USStateOrTerritoryAbbreviation[] {
  return Object.values(STATE_ABBREVIATIONS);
}

// A case-insensitive version that attempts to match
// state names regardless of capitalization
export function getStateAbbreviation(stateName: string): string {
  const normalizedInput = stateName.trim();
  
  // Try direct lookup first (for performance)
  const stateNames = Object.keys(STATE_ABBREVIATIONS) as USStateOrTerritory[];
  const exactMatch = stateNames.find(
    state => state.toLowerCase() === normalizedInput.toLowerCase()
  );
  
  if (exactMatch) {
    return STATE_ABBREVIATIONS[exactMatch].toString();
  }
  
  return "";
}

// function to return state name from abbreviation
export function getStateName(abbreviation: string): string {
  const normalizedAbbreviation = abbreviation.trim().toUpperCase();
  const stateEntries = Object.entries(STATE_ABBREVIATIONS) as [USStateOrTerritory, USStateOrTerritoryAbbreviation][];
  
  const stateEntry = stateEntries.find(
    ([, abbr]) => abbr.toUpperCase() === normalizedAbbreviation
  );

  return stateEntry ? stateEntry[0] : "";
}
